import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import {
  Search, Plus, Minus, X, ShoppingCart, Phone,
  Loader2, Package, UserPlus, CheckCircle2,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { formatCurrency } from '@/lib/utils';
import { formatSize } from '@/types/schemas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { fetchAllInventory, fetchStoreInventoryMap, mergeWithStoreStock } from '@/features/datahub/inventoryQueries';
import { findCustomerByPhone, createCustomer } from '@/features/crm/customerQueries';
import {
  addItemToCart, changeCartQty, removeFromCart as removeLine, computeTotals,
} from './cartReducers';
import type { InventoryItem, OrderItem, PaymentMethod } from '@/types/schemas';
import { cn } from '@/lib/utils';

// ── Types ──────────────────────────────────────────────────────────────────
type CartCustomer = { id?: string; name: string; phone: string };

export function POSPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentStoreId } = useCurrentStore();

  // ── Lookup (header) ──────────────────────────────────────────────────────
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  // ── Cart customer (inside cart panel) ───────────────────────────────────
  const [cartCustomer, setCartCustomer] = useState<CartCustomer | null>(null);
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);

  // ── Cart ─────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [payment, setPayment] = useState<PaymentMethod>('cash');
  const [discount, setDiscount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);

  // ── Mobile tab ───────────────────────────────────────────────────────────
  const [mobileTab, setMobileTab] = useState<'items' | 'cart'>('items');

  // ── Success modal ────────────────────────────────────────────────────────
  const [successOrder, setSuccessOrder] = useState<{ orderId: string; total: number } | null>(null);

  const { data: inventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
  });

  // Fetch per-store quantities for the selected store
  const { data: storeQtyMap = {} as Record<string, number> } = useQuery({
    queryKey: ['store-inventory-map', currentStoreId],
    queryFn: () => currentStoreId ? fetchStoreInventoryMap(currentStoreId) : Promise.resolve({} as Record<string, number>),
    enabled: !!currentStoreId,
  });

  const inventoryWithStoreQty = useMemo(
    () => mergeWithStoreStock(inventory, storeQtyMap),
    [inventory, storeQtyMap],
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inventoryWithStoreQty
      .filter((i) => i.quantity > 0 || cart.find((c) => c.itemId === i.id))
      .filter((i) => !q || `${i.itemName} ${i.sku} ${i.brand ?? ''} ${i.subBrand ?? ''}`.toLowerCase().includes(q))
      .slice(0, 80);
  }, [inventoryWithStoreQty, search, cart]);

  const { subtotal, total } = computeTotals(cart, deliveryCharge, discount);

  // ── Lookup by phone (header) ─────────────────────────────────────────────
  async function handleLookup() {
    if (!lookupPhone.trim()) return;
    setLookupLoading(true);
    try {
      const found = await findCustomerByPhone(lookupPhone.trim());
      if (found) {
        setCartCustomer({ id: found.id, name: found.name, phone: found.phone });
        setCustName(found.name);
        setCustPhone(found.phone);
        toast({ title: `Found: ${found.name}`, variant: 'success' });
      } else {
        // Pre-fill phone in cart customer area
        setCustPhone(lookupPhone.trim());
        setCustName('');
        setCartCustomer(null);
        toast({ title: 'Customer not found — fill details in cart', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Lookup failed', description: String(e), variant: 'destructive' });
    } finally {
      setLookupLoading(false);
    }
  }

  // ── Add customer from cart panel ─────────────────────────────────────────
  async function handleAddCustomer() {
    if (!custName.trim() || !custPhone.trim()) {
      toast({ title: 'Name and phone are required', variant: 'destructive' });
      return;
    }
    setAddingCustomer(true);
    try {
      // Check if already exists
      const existing = await findCustomerByPhone(custPhone.trim());
      let id: string;
      if (existing) {
        id = existing.id;
      } else {
        // Save to CRM
        id = await createCustomer({
          name: custName.trim(),
          phone: custPhone.trim(),
          email: '',
          address: '',
          source: 'manual',
        });
        qc.invalidateQueries({ queryKey: ['customers'] });
      }
      setCartCustomer({ id, name: custName.trim(), phone: custPhone.trim() });
      toast({ title: 'Customer added', variant: 'success' });
    } catch (e) {
      toast({ title: 'Failed to add customer', description: String(e), variant: 'destructive' });
    } finally {
      setAddingCustomer(false);
    }
  }

  // ── Cart helpers ─────────────────────────────────────────────────────────
  function addToCart(it: InventoryItem) {
    setCart((cur) => {
      const next = addItemToCart(cur, it);
      if (next === cur) toast({ title: 'Out of stock', variant: 'destructive' });
      return next;
    });
  }

  function changeQty(itemId: string, delta: number) {
    setCart((cur) => changeCartQty(cur, itemId, delta));
  }

  function removeFromCart(itemId: string) {
    setCart((cur) => removeLine(cur, itemId));
  }

  // ── Checkout ─────────────────────────────────────────────────────────────
  const checkout = useMutation({
    mutationFn: async () => {
      if (!cartCustomer) throw new Error('Add a customer first');
      if (!currentStoreId) throw new Error('Select a store from the topbar');
      if (cart.length === 0) throw new Error('Cart is empty');

      const ref = await addDoc(collection(db, 'orders'), {
        customerId: cartCustomer.id ?? null,
        customerSnapshot: { name: cartCustomer.name, phone: cartCustomer.phone },
        items: cart,
        subtotal,
        deliveryCharge,
        discount,
        totalAmount: total,
        paymentMethod: payment,
        storeId: currentStoreId,
        createdAt: serverTimestamp(),
        createdBy: user?.uid ?? null,
        stockDeducted: false,
      });
      return ref.id;
    },
    onSuccess: (orderId) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map', currentStoreId] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      setSuccessOrder({ orderId, total });
      // Reset
      setCart([]);
      setCartCustomer(null);
      setCustName('');
      setCustPhone('');
      setLookupPhone('');
      setDiscount(0);
      setDeliveryCharge(0);
    },
    onError: (e) =>
      toast({ title: 'Checkout failed', description: String(e), variant: 'destructive' }),
  });

  // ── Customer section (inside cart) ───────────────────────────────────────
  const customerSection = (
    <div className="border-b pb-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer</span>
        {cartCustomer && (
          <button
            onClick={() => { setCartCustomer(null); setCustName(''); setCustPhone(''); }}
            className="text-xs text-muted-foreground hover:text-destructive"
          >
            Clear
          </button>
        )}
      </div>

      {cartCustomer ? (
        // Customer attached — show info
        <div className="rounded-md border bg-gold-50 px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{cartCustomer.name}</div>
              <div className="text-xs text-muted-foreground">{cartCustomer.phone}</div>
            </div>
            <Badge variant="gold" className="text-[10px]">Added</Badge>
          </div>
        </div>
      ) : (
        // Customer form
        <div className="space-y-2">
          <Input
            placeholder="Customer name"
            value={custName}
            onChange={(e) => setCustName(e.target.value)}
            className="h-8 text-sm"
          />
          <Input
            placeholder="Phone number"
            value={custPhone}
            onChange={(e) => setCustPhone(e.target.value)}
            className="h-8 text-sm"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomer(); }}
          />
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5"
            onClick={handleAddCustomer}
            disabled={addingCustomer || !custName.trim() || !custPhone.trim()}
          >
            {addingCustomer
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <UserPlus className="h-3.5 w-3.5" />}
            Add Customer
          </Button>
        </div>
      )}
    </div>
  );

  // ── Items panel ───────────────────────────────────────────────────────────
  const itemsPanel = (
    <Card className="flex flex-col h-full">
      <CardHeader className="pb-3 pt-4">
        <CardTitle className="text-base">Add Items</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <div className="relative mb-3">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search inventory by name or SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="grid max-h-[55vh] grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
          {filteredItems.map((it) => (
            <button
              key={it.id}
              onClick={() => { addToCart(it); }}
              className="flex items-start justify-between rounded-md border bg-background p-3 text-left text-sm transition hover:border-gold-400 hover:bg-gold-50 active:bg-gold-50"
            >
              <div className="min-w-0 flex-1 pr-2">
                <div className="truncate font-medium">{it.itemName}</div>
                {/* Brand · Category */}
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[it.brand, it.subBrand, it.category].filter(Boolean).join(' · ')}
                </div>
                {/* Size + stock */}
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {formatSize(it) !== '—' && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                      {formatSize(it)}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {it.quantity} {it.unit} in stock
                  </span>
                </div>
              </div>
              <div className="shrink-0 font-medium tabular-nums text-sm">
                {formatCurrency(it.sellingPrice)}
              </div>
            </button>
          ))}
          {filteredItems.length === 0 && (
            <div className="col-span-full py-6 text-center text-sm text-muted-foreground">
              No items match.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // ── Cart panel ────────────────────────────────────────────────────────────
  const cartPanel = (
    <Card className="flex flex-col">
      <CardHeader className="pb-3 pt-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShoppingCart className="h-4 w-4" /> Cart
          {cart.length > 0 && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-gold-500 text-[11px] font-bold text-ink">
              {cart.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Customer section at top of cart */}
        {customerSection}

        {/* Cart items */}
        {cart.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">Cart is empty.</div>
        ) : (
          <div className="space-y-2 max-h-[35vh] overflow-y-auto pr-1">
            {cart.map((c) => (
              <div key={c.itemId} className="rounded-md border p-2 text-sm">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="truncate font-medium">{c.itemName}</div>
                    <div className="text-xs text-muted-foreground">{formatCurrency(c.price)} ea</div>
                  </div>
                  <button onClick={() => removeFromCart(c.itemId)} className="text-muted-foreground hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-8 w-8 sm:h-7 sm:w-7" onClick={() => changeQty(c.itemId, -1)}>
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-8 text-center tabular-nums">{c.quantity}</span>
                    <Button size="icon" variant="outline" className="h-8 w-8 sm:h-7 sm:w-7" onClick={() => changeQty(c.itemId, 1)}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <span className="font-medium tabular-nums">{formatCurrency(c.total)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Totals + payment */}
        <div className="mt-4 space-y-3 border-t pt-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="tabular-nums">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Delivery Charge ₹</span>
            <Input
              type="number"
              className="h-8 w-28 text-right"
              value={deliveryCharge}
              min="0"
              onChange={(e) => setDeliveryCharge(Number(e.target.value || 0))}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Discount ₹</span>
            <Input
              type="number"
              className="h-8 w-28 sm:w-44 text-right"
              value={discount}
              min="0"
              onChange={(e) => setDiscount(Number(e.target.value || 0))}
            />
          </div>
          <div className="flex items-center justify-between border-t pt-2 font-serif text-lg">
            <span>Total</span>
            <span className="tabular-nums">{formatCurrency(total)}</span>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Payment method</Label>
            <Select value={payment} onValueChange={(v) => setPayment(v as PaymentMethod)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="lg:hidden sticky bottom-0 bg-background pt-2 pb-safe -mx-6 px-6 border-t mt-2">
            <Button
              variant="gold"
              size="lg"
              className="w-full"
              disabled={!cartCustomer || cart.length === 0 || checkout.isPending}
              onClick={() => checkout.mutate()}
            >
              {checkout.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Checkout {formatCurrency(total)}
            </Button>
          </div>
          <div className="hidden lg:block">
            <Button
              variant="gold"
              size="lg"
              className="w-full"
              disabled={!cartCustomer || cart.length === 0 || checkout.isPending}
              onClick={() => checkout.mutate()}
            >
              {checkout.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Checkout {formatCurrency(total)}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      {/* PageHeader with inline lookup */}
      <div className="mb-4 flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-xl tracking-tight sm:text-2xl">POS</h1>
          <p className="mt-1 text-sm text-muted-foreground">Lookup, add to cart, checkout.</p>
        </div>
        {/* Lookup — inline with header */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Phone className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9 w-full sm:w-56 text-sm"
              placeholder="+91 90000 00000"
              value={lookupPhone}
              onChange={(e) => setLookupPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
            />
          </div>
          <Button
            size="sm"
            onClick={handleLookup}
            disabled={!lookupPhone.trim() || lookupLoading}
            className="shrink-0"
          >
            {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
          </Button>
        </div>
      </div>

      {/* Desktop layout — 2 containers */}
      <div className="hidden lg:grid lg:grid-cols-3 lg:gap-4">
        <div className="lg:col-span-2">{itemsPanel}</div>
        <div className="lg:col-span-1">{cartPanel}</div>
      </div>

      {/* Mobile / Tablet layout */}
      <div className="lg:hidden">
        <div className="mb-3 flex rounded-lg border bg-muted p-1">
          <button
            onClick={() => setMobileTab('items')}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors',
              mobileTab === 'items' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Package className="h-4 w-4" /> Items
          </button>
          <button
            onClick={() => setMobileTab('cart')}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors',
              mobileTab === 'cart' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <ShoppingCart className="h-4 w-4" /> Cart
            {cart.length > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gold-500 text-[11px] font-bold text-ink">
                {cart.length}
              </span>
            )}
          </button>
        </div>
        {mobileTab === 'items' ? itemsPanel : cartPanel}
      </div>

      {/* ── Success Modal ── */}
      <Dialog open={!!successOrder} onOpenChange={() => setSuccessOrder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Order Placed!
            </DialogTitle>
            <DialogDescription>
              The order has been saved successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order ID</span>
                <span className="font-medium">{successOrder?.orderId.slice(0, 8).toUpperCase()}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-serif text-lg">
                <span>Total Paid</span>
                <span className="tabular-nums">{formatCurrency(successOrder?.total ?? 0)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Payment</span>
                <span className="uppercase">{payment}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                navigate(`/pos/orders/${successOrder?.orderId}`);
                setSuccessOrder(null);
              }}
            >
              View Receipt
            </Button>
            <Button
              variant="gold"
              className="flex-1"
              onClick={() => setSuccessOrder(null)}
            >
              New Sale
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
